
import { makeStyles } from "@material-ui/core";
import { Collection } from "@type-r/models";
import { asCurrency, asTimelessDate, CollapsableSection, Flex, Link, TilesList } from "lqx-react/src";
import React from "react";
import { SmallBox } from "../../inmatch/invoices/invoicesList";
import { ListField } from "../../inmatch/matching/unreconciled/invoicesList";
import { PendingActionsInvoices } from "../api";
import { LandingPaymentInfo, PaymentRaType, UnresolvedGroupInfo } from "../payments-ras/common";

export type BucketValue = 'disputed' | 'overdue' | 'all' | 'upcoming' | 'due2weeks';

export const OpenItems = ({ title, invoices, linkUrl, bucket, hasPromiseToPayOverdue, hasBrokenPromise, hasSoaSentDate }:
    {
        title: String,
        invoices: Collection<PendingActionsInvoices>,
        linkUrl: String,
        bucket: BucketValue,
        hasPromiseToPayOverdue?: Boolean,
        hasBrokenPromise?: Boolean,
        hasSoaSentDate?: Boolean
    }) => {

    return (
        <CollapsableSection title={`${title}`}>
            <TilesList
                items={Array.from(invoices)}
                itemSize={60}
                renderer={OpenItem(linkUrl, bucket, hasPromiseToPayOverdue, hasBrokenPromise, hasSoaSentDate)}
                selectedItem={null}
                height={600} />  
        </CollapsableSection>
    );
}

const toMMDDYYYString = (date: Date) => date && `${date.getMonth() + 1}/${date.getUTCDate()}/${date.getFullYear()}`;

const OpenItem = (linkUrl, bucket, hasPromiseToPayOverdue, hasBrokenPromise, hasSoaSentDate) => ({ data, style, index }) => {
    const item = data.items[index] as PendingActionsInvoices;
    const styles = openItemStyles();
    return (
        <div style={style}>
            <SmallBox>
                <div className={styles.customerDetails} title={item.customerName}>
                    <Flex column>
                        <ListField className={styles.listField}>{item.customerName}</ListField>
                        <ListField className={styles.listField}>{item.customerExternalId}</ListField>
                    </Flex>
                </div>
                <ListField className={styles.listField}>
                    <Link
                        href={linkUrl}
                        params={
                            {
                                relationshipId: item.relationshipId,
                                bucket: bucket,
                                hasPromiseToPayOverdue: hasPromiseToPayOverdue,
                                hasBrokenPromise: hasBrokenPromise
                            }
                        }
                    >
                        View: {item.count} Invoices
                    </Link>
                </ListField>
                <ListField className={styles.amountSum}>Sum: {asCurrency(item.outstandingAmountSum)}</ListField>
                {
                    hasSoaSentDate ?
                        <ListField className={styles.listField}>Last SOA sent: {toMMDDYYYString(item.lastSoaSentDate)}</ListField>
                        : null
                }
                <Flex grow></Flex>
            </SmallBox>
        </div>
    )
}

export const PaymentOpenItems = ({ title, payments, linkUrl, type }:
    {
        title: String,
        payments: Collection<UnresolvedGroupInfo | LandingPaymentInfo>,
        linkUrl: String
        type: PaymentRaType
    }) => {

    return (
        <CollapsableSection title={`${title}`}>
            <TilesList
                items={Array.from(payments)}
                itemSize={60}
                renderer={PaymentOpenItem(linkUrl, type)}
                selectedItem={null}
                height={600} />
        </CollapsableSection>
    );
}

const PaymentOpenItem = (linkUrl: String, type: PaymentRaType) => ({ data, style, index }) => {
    const item = data.items[index];
    const styles = openItemStyles();

    const linkText = type == 'unreconciled' || type == 'unresolved-payments' ? `${item.count} payments` :
        type == 'unresolved-ras' ? `${item.count} RAs` : '';

    return (
        <div style={style}>
            <SmallBox>
                {
                    type == 'unreconciled' ?
                        <>
                            <div className={styles.customerDetails} title={item.customerName}>
                                <Flex column>
                                    <ListField className={styles.listField}>{item.customerName}</ListField>
                                    <ListField className={styles.listField}>{item.customerExternalId}</ListField>
                                </Flex>
                            </div>
                            <ListField className={styles.listField}>
                                <Link href={linkUrl} params={{ customerExternalId: item.customerExternalId }}>
                                    {linkText}
                                </Link>
                            </ListField>
                            <ListField className={styles.amountSum}>Sum: {asCurrency(item.amountSum)}</ListField>
                        </> : <></>
                }
                {
                    type == 'unresolved-payments' ?
                        <>
                            <ListField className={styles.listField}>{toMMDDYYYString(item.groupDate)}</ListField>
                            <ListField className={styles.listField}>
                                <Link href={linkUrl} params={{ valueDate: asTimelessDate(item.groupDate) }}>
                                    {linkText}
                                </Link>
                            </ListField>
                            <ListField className={styles.amountSum}>Sum: {asCurrency(item.amountSum)}</ListField>
                        </> : <></>
                }
                {
                    type == 'unresolved-ras' ?
                        <>
                            <ListField className={styles.listField}>{toMMDDYYYString(item.groupDate)}</ListField>
                            <ListField className={styles.listField}>
                                <Link href={linkUrl} params={{ createdOn: asTimelessDate(item.groupDate) }}>
                                    {linkText}
                                </Link>
                            </ListField>
                        </> : <></>
                }
                <Flex grow></Flex>
            </SmallBox>
        </div>
    )
}

const openItemStyles = makeStyles({
    customerDetails: {
        width: 220,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    },
    amountSum: {
        width: 200,
        color: "black"
    },
    listField: {
        color: "black"
    }
});
